import LmsFormGenerator, { BCFormController, IFormGeneratorFieldOptions } from "./LmsFormGenerator";
import React, { FC, useState } from "react";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { useFetchData, isEmptyValue, removeProp } from "../../../helpers/GlobalHelper";
import { GlobalConstants, LmsAllocationMethodOptions, LmsSaleTypeOptions, LmsServiceFeeMethod, LmsTransactionSplitOptions, YesNoDropdownOptions } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import LmsTable from "./LmsTable";
import { AdviceQuoteDataDisplayTable, PurchaseQuoteLoanTransactionTable, RevenueRecognitionDisplayTable } from "./LmsSharedTables";
import { FieldTypes, WidthType } from "../../interfaces/Global";
import { converToStartDateToEndDateString, convertToDateString, guid } from "../../../Helpers";

interface AmendmentFormProps {
  loanId: string;
  type: "edit" | "add" | "view";
}

export const addPhysicalAddressFields = (myForm, groupOptions = {} as any, isCountyRequired = false) => {
  myForm.addGroupFields([
    myForm.getZipCodeField(),
    myForm.getStateField(),
    myForm.getCityField(),
    myForm.getCountyField("County", { required: isCountyRequired }),
    myForm.getStreetField("Street Address", { name: "physicalStreet" }),
  ], groupOptions);
  return myForm;
};

export const addAddressFields = (myForm, groupOptions = {} as any, isCountyRequired = false) => {
  myForm.addGroupFields([
    myForm.getZipCodeField(),
    myForm.getStateField(),
    myForm.getCityField(),
    myForm.getCountyField("County", { required: isCountyRequired }),
    myForm.getStreetField(),
  ], groupOptions);
  return myForm;
};

export const addMailingIsSameAsPhysicalField = (myForm, options: IFormGeneratorFieldOptions = {}, groupOptions = {} as any) => {
  myForm.mailingIsSameAsPhysicalFieldName = options.name || "mailingIsSameAsPhysical";
  myForm.addGroupFields([
    myForm.getInputField("Mailing Address is same as Physical Address", {
      name: "mailingIsSameAsPhysical",
      field: "checkbox",
      width: 6,
      inline: true,
      isDisabledOnOperation: "isMailingSameAsPhysical",
      ...options
    }),
  ], groupOptions);
  return myForm;
};

export const addMailingAddressFields = (myForm, groupOptions = {} as any, isCountyRequired = false) => {
  myForm.addGroupFields([
    myForm.getMailingZipCodeField(),
    myForm.getMailingStateField(),
    myForm.getMailingCityField(),
    myForm.getMailingCountyField("Mailing County", { required: isCountyRequired }),
    myForm.getMailingStreetField(),
  ], groupOptions);
  return myForm;
};

export const addCommentFormFields = (myForm, borrowerOrganizationId = "") => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { name: "organizationId", disabled: true, dropdownSkipCache: true })]);
  if (borrowerOrganizationId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", { name: "specialPurposeEntityId", disabled: true, dropdownSkipCache: true })]);
  }

  myForm.addCenteredGroup([myForm.getDropdownField("Category", "orgs/list-comment-categories", { name: "organizationCommentCategoryId" })]);
  myForm.addCenteredGroup([myForm.getInputField("Subject")]);
  myForm.addCenteredGroup([myForm.getInputField("Comments", { field: "textarea", name: "description" })]);
  return myForm;
};

export const AddCommentForm = ({ orgId, borrowerOrganizationId }) => {
  const initialValue = borrowerOrganizationId ? { organizationId: borrowerOrganizationId, specialPurposeEntityId: orgId } : { organizationId: orgId };
  const myForm = addCommentFormFields(new BCFormController(initialValue, { submitSuffix: LmsEndpoints.Orgs.POST.COMMENT }), borrowerOrganizationId);

  return <LmsFormGenerator myForm={myForm} actions={["save-new", "save-close", "close"]}/>;
};

export const ViewCommentForm = ({ id, borrowerOrganizationId }) => {
  const [{ data, isLoading, error }] = useFetchData(`orgs/comment/${id}`);
  const myForm = addCommentFormFields(new BCFormController(data, { initIsDisabled: true }), borrowerOrganizationId);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["close"]}/>;
};

export const addContactFormFields = (myForm, isAdd = false, data = null as any, contactId = null as any) => {
  myForm.addGroupFields([
    myForm.getDropdownField("Salutation", "orgs/list-salutations", { required: true }),
    myForm.getInputField("First Name", { required: true }),
    myForm.getInputField("Last Name", { required: true }),
    myForm.getInputField("Email Address", { name: "email", validations: ["required", "email"] }),
    myForm.getDisplayField("Linked to User Account", { name: "linkedUserAccountDisplay", isHidden: { isLinkedToUserAccount: true }, isHiddenOperation: "notequal" }),
    myForm.getBlankField({ isHidden: { isLinkedToUserAccount: true } })
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Role in Organization", { name: "positionInCompany" }),
    myForm.getCheckboxField("Is Active", isAdd ? { value: true } : {}),
    myForm.getCheckboxField("Create User", { isHidden: { isLinkedToUserAccount: true } }),
    myForm.getCheckboxField("Organization Owner"),
    myForm.getCheckboxField("Do Not Call"),
    myForm.getBlankField({ isHidden: { isLinkedToUserAccount: true }, isHiddenOperation: "notequal" })
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Password", { fieldType: "password" }),
    myForm.getInputField("Confirm Password", { fieldType: "password", validations: ["confirmPassword"] }),
    myForm.getDropdownField("Role", isAdd ? "orgs/list-roles?orgId={id}&action=Add" : `orgs/list-roles?contactId=${contactId}`, isAdd ? { dependencyField: "organizationId" } : {}),
    myForm.getCheckboxField("Is External User", { value: true }),
    myForm.getBlankField()
  ], { isHidden: { createUser: true }, isHiddenOperation: "notequal" });
  myForm.addAddressSectionToForm();
  myForm.addGroupFields([
    myForm.getInputField("Business Phone", { fieldType: "phone", name: "officePhone", required: true }),
    myForm.getInputField("Mobile Phone", { fieldType: "phone" }),
    myForm.getInputField("Home Phone", { fieldType: "phone" }),
    myForm.getInputField("Amendment", { name: "officePhoneExtension", validations: ["amendment"] }),
    myForm.getDropdownField("Preferred Loan Officer", "orgs/list-preferred-loan-officers", { name: "preferredLoanOfficerOrganizationContactId" }),
  ]);
  myForm.addGroupFields([
    myForm.getInputField("Excess Draw Approval Percent", { fieldType: "percent", value: 10, required: true }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField()
  ], { isHidden: { organizationTypeId: GlobalConstants.OrganizationTypes["Loan Company"] }, isHiddenOperation: "notequal" });
  myForm.addLabelField("Preferred Contact Method");
  myForm.addGroupFields([
    myForm.getGroupRadioField("Email"),
    myForm.getGroupRadioField("Telephone"),
  ], { radioGroupName: "preferredContactMethodOption", radioGroupValue: data?.preferredContactMethodOption || "", widths: 1 });
  return myForm;
};

export const addStateTaxFields = (myForm, borrowerOrganizationId) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", {
    name: "organizationId",
    disabled: true,
    required: true,
    dropdownSkipCache: true
  })]);

  if (borrowerOrganizationId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", {
      name: "specialPurposeEntityId",
      disabled: true,
      required: true,
      dropdownSkipCache: true
    })]);
  }

  myForm.addCenteredGroup([myForm.getDropdownField("State", "orgs/list-states", { required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Tax ID", { name: "organizationStateTaxNumber", required: true })]);
  return myForm;
};

export const AddStateTaxForm = ({ orgId = null, borrowerOrganizationId = "" }) => {
  const initialValue = borrowerOrganizationId ? { organizationId: borrowerOrganizationId, specialPurposeEntityId: orgId } : { organizationId: orgId };
  const myForm = addStateTaxFields(new BCFormController(initialValue, {
    submitSuffix: LmsEndpoints.Orgs.POST.STATE_TAX
  }), borrowerOrganizationId);
  return <LmsFormGenerator myForm={myForm} actions={["save-new", "save-close", "close"]}/>;
};

export const EditStateTaxForm = ({ id, borrowerOrganizationId = "" }) => {
  const [{ data, isLoading, error }] = useFetchData(`orgs/state-tax/${id}`);
  const myForm = addStateTaxFields(new BCFormController(data, {
    submitSuffix: LmsEndpoints.Orgs.PUT.STATE_TAX,
    deleteSuffix: "orgs/state-taxes",
    clearSuffixesOnSubmit: [`orgs/state-tax/${id}`]
  }), borrowerOrganizationId);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["save-new", "save-close", "delete", "close"]} />;
};

export const addInsuranceFormFields = (myForm: BCFormController, isBorrowerForm = false, borrowerOrganizationId = "") => {
  myForm.addCenteredGroup([myForm.getDropdownField("Organization", "orgs/list-orgs", { name: "organizationId", disabled: true, dropdownSkipCache: true })]);
  if (borrowerOrganizationId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", {
      name: "specialPurposeEntityId",
      disabled: true,
      dropdownSkipCache: true
    })]);
  }

  if (!borrowerOrganizationId) {
    myForm.addCenteredGroup([myForm.getDropdownField("Coverage Type", "metadata/insurance-metadata", { dropdownOptionsSubPath: "coverageTypes", name: "policyCoverageId" })]);
    myForm.addCenteredGroup([myForm.getInputField("Application #", { name: "applicationNumber" })], {
      isHidden: { policyCoverageId: GlobalConstants.InsuranceMetadata.CoverageTypes.Property },
      isHiddenOperation: "notequal"
    });
    myForm.addCenteredGroup([myForm.getInputField("Loan #", { name: "loanNumber" })], {
      isHidden: { policyCoverageId: GlobalConstants.InsuranceMetadata.CoverageTypes.Property },
      isHiddenOperation: "notequal"
    });
    if (isBorrowerForm && !borrowerOrganizationId) {
      myForm.addCenteredGroup([myForm.getDropdownField("Special Purpose Entity", LmsEndpoints.Orgs.GET.LIST_SPES, {
        dependencyField: "organizationId",
        isDisabledOn: { policyCoverageId: GlobalConstants.InsuranceMetadata.CoverageTypes.Property }
      })]);
    }
    myForm.addCenteredGroup([myForm.getDropdownField("Loan Property", "loans/loan-properties", { dependencyField: "loanId" })], {
      isHidden: { policyCoverageId: GlobalConstants.InsuranceMetadata.CoverageTypes.Property },
      isHiddenOperation: "notequal"
    });
  }

  myForm.addCenteredGroup([myForm.getDropdownField("Policy Type", "metadata/insurance-metadata", { required: true, dropdownOptionsSubPath: "policyTypes" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Provider", "metadata/insurance-metadata", { required: true, dropdownOptionsSubPath: "providers", name: "insuranceBrokerOrganizationId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Provider Agent", "metadata/list-provider-agents", { name: "insuranceBrokerOrganizationContactId", dependencyField: "insuranceBrokerOrganizationId", required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Policy #", { required: true, name: "policyNumber" })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Expiration Date", { required: true, name: "expiryDate" })]);
  myForm.addCenteredGroup(
    [
      myForm.getCurrencyField("Coverage Amount", { required: true })
    ], { isHidden: { policyTypeId: GlobalConstants.InsuranceMetadata.PolicyTypes.Liability } }
  );

  myForm.addCenteredGroup([myForm.getCurrencyField("Coverage Amount Per Occurence", { required: true })], {
    isHidden: { policyTypeId: GlobalConstants.InsuranceMetadata.PolicyTypes.Liability },
    isHiddenOperation: "notequal"
  });
  myForm.addCenteredGroup([myForm.getCurrencyField("Coverage Amount Aggregate", { required: true })], {
    isHidden: { policyTypeId: GlobalConstants.InsuranceMetadata.PolicyTypes.Liability },
    isHiddenOperation: "notequal"
  });
  return myForm;
};

const cleanLoanDetails = (options) => {
  options?.setInputs(removeProp(options?.inputs, ["applicationNumber", "loanId", "loanPropertyId"]));
};

const handleFieldBlur = async (fieldName, fieldValue, options) => {
  const loanNumber = options?.inputs?.loanNumber;
  const applicationNumber = options?.inputs?.applicationNumber;
  if (fieldName === "loanNumber") {
    if (loanNumber) {
      const loanDetails = await options?.fetchData(`loans/loan-details-by-loanno-or-appno?loanNumber=${loanNumber}`);
      if (loanDetails) {
        if (loanDetails?.data?.applicationNumber) {
          options?.setInputs({ ...options?.inputs, applicationNumber: loanDetails?.data?.applicationNumber, loanId: loanDetails?.data?.loanId, loanPropertyId: "" });
        }
        else {
          cleanLoanDetails(options);
        }
      }
      else {
        if (options?.inputs?.loanId) {
          cleanLoanDetails(options);
        }
      }
    }
    else {
      cleanLoanDetails(options);
    }
  }
  else if (fieldName === "applicationNumber") {
    const loanDetails = await options?.fetchData(`loans/loan-details-by-loanno-or-appno?appNumber=${applicationNumber}`);
    if (loanDetails) {
      if (loanDetails?.data?.loanNumber) {
        options?.setInputs({ ...options?.inputs, loanNumber: loanDetails?.data?.loanNumber, loanId: loanDetails?.data?.loanId });
      }
    }
    else {
      if (options?.inputs?.loanId) {
        options?.setInputs(removeProp(options?.inputs, ["loanNumber", "loanPropertyId", "loanId"]));
      }
    }
  }
};

export const AddInsuranceForm = ({ orgId, isBorrowerForm = false, borrowerOrganizationId = "" }) => {
  const organizationId = borrowerOrganizationId ? borrowerOrganizationId : orgId;
  const initialValue = borrowerOrganizationId ? { organizationId, specialPurposeEntityId: orgId, coverageAmount: 0 } : { organizationId, coverageAmount: 0 };
  const myForm = addInsuranceFormFields(new BCFormController(initialValue, {
    submitSuffix: LmsEndpoints.Orgs.POST.INSURANCE,
  }), isBorrowerForm, borrowerOrganizationId);
  const formActions = ["save-new", "save-close", "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} onBlur={handleFieldBlur}/>;
};

export const EditInsuranceForm = ({ id, isBorrowerForm = false, borrowerOrganizationId = "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`orgs/insurance/${id}`);
  const myForm = addInsuranceFormFields(new BCFormController(data, {
    submitSuffix: LmsEndpoints.Orgs.PUT.UPDATE_INSURANCE,
    deleteSuffix: "orgs/insurances",
    clearSuffixesOnSubmit: [`orgs/insurance/${id}`],
    blankWidth: 6
  }), isBorrowerForm, borrowerOrganizationId);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} />;
};

export const addAssetsLiabilityFields = (myForm) => {
  myForm.addCenteredGroup([myForm.getDropdownField("AL Class", "orgs/list-al-classes", { required: true, name: "alclassId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("AL Type", "orgs/list-al-types", { required: true, name: "altypeId", dependencyField: "alclassId" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Status", "orgs/list-al-statuses", { name: "alstatusId" })]);
  myForm.addCenteredGroup([myForm.getInputField("Name of Institution", { required: true, name: "institution" })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Current Balance", { required: true, name: "value" })]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Monthly Liability")]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);
  return myForm;
};

export const handleOnScheduleReoBlur = (name, value, options) => {
  if (name === "marketValue" && !isEmptyValue(value) && value < 1) {
    options.setInputs({ ...options?.inputs, marketValue: 1 });
  }
  else if (name === "percentageOwned" && !isEmptyValue(value) && parseInt(value?.replaceAll(",", "")) >= 10000) {
    options.setInputs({ ...options?.inputs, percentageOwned: 9999.99 });
  }
};

export const addMortgageTableFormFields = (myForm) => {
  const requiredFunc = ({ inputs, fieldName } : any) => {
    if (fieldName === "mortgageHolder1") {
      return !isEmptyValue(inputs?.mortgageAmount1) && parseFloat(inputs?.mortgageAmount1) > 0;
    }
    else if (fieldName === "mortgageHolder2") {
      return !isEmptyValue(inputs?.mortgageAmount2) && parseFloat(inputs?.mortgageAmount2) > 0;
    }
    else if (fieldName === "mortgageHolder3") {
      return !isEmptyValue(inputs?.mortgageAmount3) && parseFloat(inputs?.mortgageAmount3) > 0;
    }
  };

  const validations = [{ required: requiredFunc }];
  const positiveNumberValidationFunc = ({ inputs, fieldName } : any) => {
    if (fieldName === "mortgagePayment1") {
      return parseFloat(inputs?.mortgageAmount1) > 0 && !(parseFloat(inputs?.mortgagePayment1) > 0);
    }
    else if (fieldName === "mortgagePayment2") {
      return parseFloat(inputs?.mortgageAmount2) > 0 && !(parseFloat(inputs?.mortgagePayment2) > 0);
    }
    else if (fieldName === "mortgagePayment3") {
      return parseFloat(inputs?.mortgageAmount2) > 0 && !(parseFloat(inputs?.mortgagePayment3) > 0);
    }
  };

  myForm.addGroupFields([
    myForm.getBlankField({ width: 3 }),
    myForm.getLabelField("Amount", { position: "center", required: true }),
    myForm.getLabelField("Mortgage Holder", { position: "center" }),
    myForm.getLabelField("Monthly Payment", { position: "center", required: true }),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("1st Mortgage", { width: 3, position: "center" }),
    myForm.getCurrencyField("", { name: "mortgageAmount1", errorLabel: "The Mortgage Amount1", required: true }),
    myForm.getInputField("", { name: "mortgageHolder1", errorLabel: "Mortgage Holder 1", validations }),
    myForm.getCurrencyField("", {
      name: "mortgagePayment1",
      errorLabel: "The Mortgage Payment1",
      validations: ["required", { positiveNumber: positiveNumberValidationFunc }]
    }),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("2nd Mortgage", { width: 3, position: "center" }),
    myForm.getCurrencyField("", { name: "mortgageAmount2", errorLabel: "The Mortgage Amount2", required: true }),
    myForm.getInputField("", { name: "mortgageHolder2", errorLabel: "Mortgage Holder 2", validations }),
    myForm.getCurrencyField("", {
      name: "mortgagePayment2",
      errorLabel: "The Mortgage Payment2",
      validations: ["required", { positiveNumber: positiveNumberValidationFunc }]
    }),
  ]);
  myForm.addGroupFields([
    myForm.getLabelField("3rd Mortgage", { width: 3, position: "center" }),
    myForm.getCurrencyField("", { name: "mortgageAmount3", errorLabel: "The Mortgage Amount3", required: true }),
    myForm.getInputField("", { name: "mortgageHolder3", errorLabel: "Mortgage Holder 3", validations }),
    myForm.getCurrencyField("", {
      name: "mortgagePayment3",
      errorLabel: "The Mortgage Payment3",
      validations: ["required", { positiveNumber: positiveNumberValidationFunc }]
    }),
  ]);
  myForm.addGroupFields([
    myForm.getBlankField({ width: 3 }),
    myForm.getCurrencyField("", { name: "totalAmount", calculatedFields: ["mortgageAmount1", "mortgageAmount2", "mortgageAmount3"], disabled: true }),
    myForm.getBlankField(),
    myForm.getCurrencyField("", { name: "totalMonthlyPayment", calculatedFields: ["mortgagePayment1", "mortgagePayment2", "mortgagePayment3"], disabled: true }),
  ]);
  return myForm;
};

const addProformaFields = (myForm: BCFormController, loanId: string) => {
  myForm.addCenteredGroup([myForm.getDisplayField("Document Master", { disabled: true, value: "Proforma" })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Document Category", { disabled: true, value: "Credit Review" })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan", "document/list-allowed-proforma-loans", { required: true, disabled: !!loanId })]);
  myForm.addCenteredGroup([myForm.getFileField("Attachment", { name: "attachmentFile", required: true })]);
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { isDefaultDateNow: true, required: true }), myForm.getCalendarField("Expiration Date", { isDefaultDateNow: true, required: true })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Archive"), myForm.getCheckboxField("Include in LRC", { value: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Result", { name: "description" })]);
  return myForm;
};

export const AddProformaForm = ({ loanId = "" }) => {
  const myForm = addProformaFields(new BCFormController({ loanId }, { 
    submitMethod: "POST", 
    payloadDataType: "FormData",
    submitSuffix: "document/upload-loan-data-cash-flow", 
    onSubmitSuccess: (response, options) => { console.log("r", response); options.history.push(`/documents-upload-loan-data-cash-flow/${response?.dataUploadId}`);} // TODO: Sprint Work 14511
  }), loanId);

  return <LmsFormGenerator myForm={myForm} actions={[{text: "Save and Continue", action: "save" }, "close"]}/>;
};

export const LmsLoanReviewApplicationForm = ({ loanId = ""}) => {
  const [{ data: loanReviewData, isLoading, error }] = useFetchData(`voting/loan-review/${loanId}`);
  const [{ data: loanBudget}] = useFetchData(`voting/loan-polling-info/${loanId}`);
  const borrower = loanReviewData?.borrowerOrganizationId;
  const [{ data: borrowerData }] = useFetchData(borrower ? `orgs/borrower/${borrower}`: "");
  const loanDetailId = loanReviewData?.loanDetailId || "";
  const loanReviewDataDetails = {...loanReviewData, ...loanBudget, ...borrowerData};
  const myForm = new BCFormController(loanReviewDataDetails, { initIsDisabled: true, values : { state_input: loanReviewData?.state?.display, city_input: loanReviewData?.city?.display }});
  myForm.addGroupFields([
    myForm.getInputField("Borrower", { name: "name" }),
    myForm.getInputField("Application", { name: "applicationNumberDisplay" }),
    myForm.getInputField("Project Name", { name: "name" }),
    myForm.getTextareaField("Property", { name: "loanProperty_Streets" }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("City", { name: "city_input" }),
    myForm.getInputField("State", { name: "state_input" }),
    myForm.getInputField("Submarket", { name: "loanProject_Submarket" }),
    myForm.getInputField("Budget", { fieldType: "dollar", name: "loanBudgetAmount" }),
  ]);

  myForm.addGroupFields([
    myForm.getDisplayField("Original Projected IRR", { name:"loanUnderWriting_ProjectedIRrOriginal" }),
    myForm.getInputField("Current LTV", { name: "loanUnderWriting_LtvPercentage" }),
    myForm.getInputField("Current CLTV", { name: "loanUnderWriting_CltVPercentage" }),
    myForm.getInputField("Current LTC", { name: "loanUnderWriting_LtvPercentage" }),
  ]);

  myForm.addGroupFields([
    myForm.getCheckboxField("Direct Pay", { inline: true }),
    myForm.getInputField("How many homes have Guarantors completed and sold in last 18 months?", { width: 8 }),
    myForm.getInputField("Approved Liquidity Percentage", { fieldType:"percent", name: "loanUnderWriting_ApprovedLiquidityPercentage", width: 4 }),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("LRC Overview", { name:"loanUnderWriting_LrcOverview", width: 16 }),
  ]);

  const formActions = [
    "close",
  { text: "View Loan Details", icon: GlobalConstants.Icons.CARET_UP, path: `/loandetails/${loanDetailId}`}
];

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} />;
};

const HELP_AMENDMENT_BUTTON = {
  text: "Help",
  icon: GlobalConstants.Icons.QUESTION,
  handleClick: () => null,
};

const AmendmentBudget = ({ handleClose, options }) => {
  // const [{ data: budget }] = useFetchData(`/loans/amendments/extension-budgets/${options.inputs.id}`);
  const form = new BCFormController(
    {},
    { submitSuffix: "/loans/amendments/extension-budgets", isCompact: true }
  );
  const budget = new Array(10).fill(null);

  if (FeatureFlags.IS_DEBUG) {
    console.log({ options });
  }

  form.addGroupFields([
    form.getCurrencyField("Modify Budget", { disabled: true }),
    form.getCurrencyField("Left To Allocate", { disabled: true }),
    form.getCheckboxField("Hide Paid Off Properties", { position: "center" }),
  ]);

  form.addGroupFields(
    [
      ...(
        [
          ["Property", 3],
          ["Lot", 4],
          ["Budget Amount", 3],
          ["Modify Budget by", 3, true],
          ["New Budget", 3],
          ["All Units Paid", 3],
        ] as [string, WidthType, boolean][]
      ).map(([label, width, required]) =>
        form.getLabelField(label, { position: "center", width, required })
      ),
    ],
    { widths: 16 }
  );

  if (budget.length > 0) {
    budget.forEach(() => {
      form.addGroupFields([
        form.getLabelField("property", {
          width: 3,
          position: "center",
          noBackground: true,
        }),
        form.getLabelField("deleted", {
          width: 4,
          position: "center",
          noBackground: true,
        }),
        form.getCurrencyField("Budget Amount", {
          label: "",
          width: 3,
          value: 1000,
          name: "amount",
          disabled: true,
          // nestedInputsProp: id,
          calculatedTargetField: "totalBudgetAmount",
        }),
        form.getCurrencyField("Modify Budget by", {
          label: "",
          width: 3,
          value: 0,
          name: "modifyBy",
          // nestedInputsProp: id,
          calculatedTargetField: "totalModifyByAmount",
        }),
        form.getCurrencyField("New Budget", {
          label: "",
          width: 3,
          value: 1000,
          disabled: true,
          name: "newAmount",
          // nestedInputsProp: id,
          calculatedTargetField: "totalNewBudgetAmount",
        }),
        form.getCheckboxField("All Units Paid", {
          label: "",
          width: 3,
          value: false,
          disabled: true,
          position: "center",
          noBackground: true,
          // nestedInputsProp: id,
        }),
      ]);
    });
  }

  return (
    <LmsFormGenerator
      handleClose={handleClose}
      myForm={form}
      actions={["close", HELP_AMENDMENT_BUTTON]}
    />
  );
};

const AmendmentDocumentForm = ({ handleClose, loanExtensionId }) => {
  const form = new BCFormController(
    {},
    { submitSuffix: `/loandetails/servicing/add-document/${loanExtensionId}` }
  );

  form.addCenteredGroup([
    form.getDropdownField("Document Master"), // TODO: add dropdown options
  ]);

  form.addCenteredGroup([form.getFileField("Document")]);

  return (
    <LmsFormGenerator myForm={form} actions={["save-close", "close"]} handleClose={handleClose} />
  );
};

export const amendmentFields = (myForm: BCFormController, data): BCFormController => {
  myForm.addGroupFields([
    ...[
      ["Borrower", "borrowerOrganization"],
      ["Special Purpose Entity", "specialPurposeEntity"],
      ["Loan", "display"],
    ].map(([label, name]) => myForm.getDisplayField(label, { noBackground: true, name })),
    myForm.getCurrencyField("Original Loan Amount", {
      noBackground: true,
      name: "loanAmountOriginal",
    }),
  ]);

  myForm.addDividerField();
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Term Changing", {
        name: "isTermChanging",
        field: "checkbox",
        inline: true,
      }),
    ],
    { widths: "4" }
  );
  myForm.addGroupFields(
    [
      myForm.getCalendarField("Submission Date", { disabled: true }),
      myForm.getCalendarField("Current Maturity", { disabled: true }),
      myForm.getCalendarField("Implemented On"),
      myForm.getNumberField("Amendment Days", {
        name: "extensionDays",
        required: true,
        onFieldChanged: ({ value, setInputs }) => {
          if (!Number.isNaN(value)) {
            const d = new Date(data.originalProposedMaturityDate);
            d.setDate(d.getDate() + value);
            setInputs((prevInputs) =>
              Object.assign(prevInputs, { proposedMaturityDate: d.toISOString() })
            );
          } else {
            setInputs((prevInputs) =>
              Object.assign(prevInputs, {
                proposedMaturityDate: data.originalProposedMaturityDate,
              })
            );
          }
        },
      }),
    ],
    { isHidden: { isTermChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getCalendarField("Proposed Maturity Date", { disabled: true }),
      myForm.getCalendarField("Amendment Date", { name: "extensionDate", required: true }),
    ],
    { widths: 4, isHidden: { isTermChanging: false } }
  );

  myForm.addDividerField();
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Rate Changing", {
        name: "isRateChanging",
        field: "checkbox",
        inline: true,
      }),
    ],
    { widths: "4" }
  );
  myForm.addGroupFields(
    [
      myForm.getDisplayField("Original Interest Rate", {
        fieldType: "float",
        name: "interestRateOriginal",
      }),
      myForm.getDisplayField("Current Interest Rate", {
        fieldType: "float",
        name: "interestRateCurrent",
      }),
      myForm.getCalendarField("Rate Change Implemented On", { disabled: true }),
      myForm.getBlankField(),
    ],
    { isHidden: { isRateChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Rate Change %", {
        disabled: true,
        fieldType: "percent",
        name: "extensionRateChangePercentage",
      }),
      myForm.getInputField("Revised Rate", {
        required: true,
        fieldType: "float",
        name: "newInterestRate",
        onFieldChanged: ({ value, setInputs }) => {
          const parsedValue = parseFloat(value);
          if (!Number.isNaN(parsedValue)) {
            setInputs((prevInputs) =>
              Object.assign(prevInputs, {
                extensionRateChangePercentage: parsedValue - (data.interestRateCurrent || 0),
              })
            );
          } else {
            setInputs((prevInputs) =>
              Object.assign(prevInputs, {
                extensionRateChangePercentage: data.originalExtensionRateChangePercentage,
              })
            );
          }
        },
      }),
      myForm.getCalendarField("Requested Effective Rate Change", {
        required: true,
        name: "requestedRateChangeEffectiveDate",
        onFieldChanged: ({ value, setInputs }) => {
          setInputs((prevInputs) =>
            Object.assign(prevInputs, {
              rateChangeEffectiveDate: value,
            })
          );
        },
      }),
      myForm.getCalendarField("Effective Rate Change", {
        required: true,
        name: "rateChangeEffectiveDate",
      }),
    ],
    { isHidden: { isRateChanging: false } }
  );

  myForm.addDividerField();
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Budget Changing", {
        field: "checkbox",
        name: "isBudgetChanging",
        inline: true,
      }),
    ],
    { widths: "4" }
  );
  myForm.addGroupFields(
    [
      myForm.getInputField("Modify Budget by", {
        name: "budgetChangeAmount",
        fieldType: "dollar",
        required: true,
      }),
      myForm.getInputField("Original Budget Amount", { fieldType: "dollar" }),
      myForm.getInputField("Amended Budget Amount", { fieldType: "dollar", required: true }),
    ],
    { widths: 4, isHidden: { isBudgetChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Budget Changes Completed", {
        field: "checkbox",
        inline: true,
      }),
      myForm.getCalendarField("Requested Effective Budget Increase Date", {
        name: "requestedBudgetChangeEffectiveDate",
        required: true,
      }),
      myForm.getCalendarField("Effective Budget Increase Date", {
        required: true,
        name: "budgetChangeEffectiveDate",
      }),
      myForm.getCalendarField("Budget Change Implemented On"),
    ],
    { isHidden: { isBudgetChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getButtonField("Modify Budget", () => null, {
        width: 4,
        fullWidth: true,
        modalContent(setIsAddFormModalOpen, options?) {
          return (
            <AmendmentBudget handleClose={() => setIsAddFormModalOpen(false)} options={options} />
          );
        },
      }),
    ],
    { isHidden: { isBudgetChanging: false } }
  );
  myForm.addDividerField();
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Interest Changing", {
        name: "isInterestReserveChanging",
        field: "checkbox",
        inline: true,
      }),
    ],
    { widths: "4" }
  );
  myForm.addGroupFields(
    [
      myForm.getInputField("Change Amount", {
        name: "interestReserveChangeAmount",
        fieldType: "dollar",
        required: true,
      }),
      myForm.getInputField("Original Interest Reserve", {
        fieldType: "dollar",
        disabled: true,
        name: "originalInterestReserveAmount",
      }),
      myForm.getInputField("Amended Interest Reserves	", {
        fieldType: "dollar",
        disabled: true,
        name: "amendedInterestReserveAmount",
      }),
    ],
    { widths: 4, isHidden: { isInterestReserveChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Current Interest Reserve Balance", {
        fieldType: "dollar",
        name: "currentInterestReserveAmount",
        disabled: true,
      }),
      myForm.getCalendarField("Requested Effective Interest Reserve Date", {
        name: "requestedInterestReserveChangeEffectiveDate",
      }),
      myForm.getCalendarField("Effective Interest Reserve Date", {
        required: true,
        name: "interestReserveChangeEffectiveDate",
      }),
    ],
    { widths: 4, isHidden: { isInterestReserveChanging: false } }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Do you want to apply IR to pastdue interest and fees?", {
        name: "applyInterestReserveToOutstandingTransactions",
        field: "checkbox",
        inline: true,
      }),
      myForm.getCalendarField("Interest Reserve Change Implemented On", { width: 4 }),
    ],
    { isHidden: { isInterestReserveChanging: false } }
  );

  myForm.addDividerField();
  myForm.addLabelField("What are the fee terms");
  myForm.addGroupFields(
    [
      myForm.getDropdownField(
        "Payment Method",
        [
          { key: "borrowerPays", text: "Borrower Pays", value: "borrowerPays" },
          { key: "paidAtPayoff", text: "Paid at Payoff", value: "paidAtPayoff" },
        ],
        { required: true }
      ),
      myForm.getDropdownField(
        "Fee Based Off",
        [
          {
            key: "originalCommittedLoanAmount",
            text: "Original Committed Loan Amount",
            value: "originalCommittedLoanAmount",
          },
          { key: "currentBalance", text: "Current Balance", value: "currentBalance" },
        ],
        { required: true }
      ),
      myForm.getInputField("Prorate Fee At Payoff", {
        field: "checkbox",
        inline: true,
      }),
    ],
    { widths: 4 }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Fee %", {
        fieldType: "percent",
        name: "extensionFeePercentage",
        required: true,
      }),
      myForm.getInputField("Fee Amount", {
        name: "extensionFeeAmount",
        fieldType: "dollar",
      }),
      myForm.getCalendarField("Requested Effective Fee Date", {
        name: "requestedExtensionFeeEffectiveDate",
      }),
      myForm.getCalendarField("Effective Fee Date", {
        name: "extensionFeeEffectiveDate",
        required: true,
      }),
    ],
    { widths: 4 }
  );

  myForm.addGroupFields([
    myForm.getDisplayField("Original Loan Amount", {
      name: "loanAmountOriginal",
      fieldType: "dollar",
    }),
    myForm.getDisplayField("Amendment Loan Amount", { fieldType: "dollar" }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Other Fees", { name: "otherFees" }),
    myForm.getCurrencyField("Current Loan Amount", {
      name: "loanAmountCurrent",
      disabled: true,
      noBackground: true,
    }),
    myForm.getDisplayField("Current Loan + Fees", { fieldType: "dollar" }),
    myForm.getDisplayField("Over by", { fieldType: "dollar", disabled: true }),
  ]);

  myForm.addDividerField();
  myForm.addLabelField("Review");
  myForm.addGroupFields(
    [
      myForm.getDropdownField("Amendment Type", "/metadata/list-extension-types", {
        required: true,
        name: "extensionTypeId",
      }),
      myForm.getCalendarField("Executed Loan Amendment Date", {
        name: "extensionTypeId",
      }),
      myForm.getInputField("Executed Loan Amendment Agreement", {
        name: "isExecutedLoanAmendmentAgreement",
        field: "checkbox",
        inline: true,
      }),
    ],
    { widths: 4 }
  );

  myForm.addGroupFields([myForm.getTextareaField("Extension Reason", { width: 16 })]);
  myForm.addGroupFields([
    myForm.getCalendarField("Amendment Signature Received Date", {
      name: "extensionSignatureReceivedDate",
    }),
    myForm.getInputField("First Amendment Recorded", {
      name: "",
      field: "checkbox",
      inline: true,
    }),
    myForm.getCalendarField("Received DOT Amendment Date", {
      name: "receivedDotamendmentDate",
    }),
    myForm.getInputField("Title Updated", {
      field: "checkbox",
      inline: true,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Amendment Signature Received", {
      name: "extensionSignatureReceived",
      field: "checkbox",
      inline: true,
    }),
    myForm.getInputField("LMS Updated", {
      name: "lmsupdated",
      field: "checkbox",
      inline: true,
    }),
    myForm.getInputField("Manual Delivery", {
      field: "checkbox",
      inline: true,
    }),
    myForm.getInputField("Budget Updated", {
      field: "checkbox",
      inline: true,
    }),
  ]);

  myForm.addGroupFields([myForm.getTextareaField("Review Comments", { width: 16 })]);
  myForm.addGroupFields([
    myForm.getDropdownField("Review Decision", "/metadata/list-extension-decisions", {
      name: "extensionDecisionId",
    }),
    myForm.getCalendarField("Review Date"),
    myForm.getDisplayField("LRC Comments"),
    myForm.getDisplayField("Loan Purchaser Decision"),
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("Amendment Status", "/metadata/list-extension-statuses", {
      name: "extensionStatusId",
    }),
  ]);
  myForm.addGroupFields([
    myForm.getButtonField("View Comments", () => null, { width: 4, fullWidth: true }),
  ]);
  myForm.addGroupFields([
    myForm.getComponent(
      <LmsTable apiSuffix="" key="" rowTitle="Amendment Comments" compact isDisabled />,
      { width: 8 }
    ),
  ]);
  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getButtonField("Add Document", () => null, {
      width: 4,
      fullWidth: true,
      modalContent(setIsAddFormModalOpen, options) {
        return (
          <AmendmentDocumentForm
            loanExtensionId={options.inputs.id}
            handleClose={() => setIsAddFormModalOpen(false)}
          />
        );
      },
    }),
  ]);

  return myForm;
};

export const AmendmentForm: FC<AmendmentFormProps> = ({ loanId, type }) => {
  const [
    {
      data = {
        isTermChanging: false,
        isRateChanging: false,
        isBudgetChanging: false,
        isInterestReserveChanging: false,
      },
      isLoading,
      error,
    },
  ] = useFetchData(`/amendments/${type === "add" ? "initial-data/" : ""}${loanId}`);

  const myForm = amendmentFields(
    new BCFormController(data, {
      submitSuffix: "amendments",
      submitMethod: type === "add" ? "POST" : "PUT",
      values: { loanId: data?.loanId, paymentMethodOption: data?.paymentMethodOption, feeBasedOffOption: data?.feeBasedOffOption },
    }),
    {
      ...data,
      originalExtensionRateChangePercentage: data.extensionRateChangePercentage || null,
    }
  );

  const handleSendToLRC = (data, options, useFormInstance) => { 
    useFormInstance.handleSubmit("amendments/send-to-lrc", { buttonText: options.buttonText, data, method: "PUT"});
  };

  return (
    <LmsFormGenerator
      error={error}
      isLoading={isLoading}
      myForm={myForm}
      actions={[
        "save",
        "save-close",
        ...(type === "edit"
          ? [
              {
                text: "Send To LRC",
                icon: GlobalConstants.Icons.SAVE,
                handleClick: handleSendToLRC,
              },
              {
                text: "Generate Note Amendment",
                action: "download",
                path: `loandetails/amendment/${loanId}/download-note-amendment?newInterestRate=${data?.newInterestRate || ""}`,
                icon: GlobalConstants.Icons.EXCEL_OUTLINE,
              },
            ]
          : []),
        "close",
        HELP_AMENDMENT_BUTTON,
      ]}
    />
  );
};

export const BudgetDetailsPage = ({ documentId, handleClose }) => {
  const [{ data, isLoading, error }] = useFetchData(documentId ? `document/loan-data-cash-flow/${documentId}` : "");
  const initialData = data ? { ...data, createdOn:  convertToDateString(data?.createdOn, "datetimewithoutsecond") } : {};
  const myForm = new BCFormController(initialData, { blankWidth: 1 });
  myForm.addCenteredGroup([
    myForm.getLinkField("Attachment", {
      linkType: "DownloadFile",
      name: "attachmentFileName",
      link: `budget/uploads/${documentId}/export`,
      method: "POST"
    }),
    myForm.getDisplayField("Result", { name: "description" })
  ]);
  myForm.addCenteredGroup([
    myForm.getDisplayField("Created By"),
    myForm.getDisplayField("Created On")
  ]);

  return <>
    <LmsFormGenerator myForm={myForm} error={error} isLoading={isLoading} actions={["close"]} handleClose={handleClose} />
    <LmsTable apiSuffix={documentId ? `budget/uploads/${documentId}/data` : ""} title="Details" actionButtons={["export"]} skipClick/>
  </>;
};

export const UploadBudgetForm = (
  {
    loanPropertyData,
    loanId = "",
    setIsModalOpen = undefined as any,
    handleClose = undefined as any,
    isDisplayedInModal = false,
  }) => {
  const [isDetailsPageOpen, setIsDetailsPageOpen] = useState(false);
  const [documentId, setDocumentId] = useState("");
  const loanPropertyId = loanPropertyData?.id;
  const parentLoanId = loanPropertyData?.loanId || loanId;

  if (!handleClose && setIsModalOpen) {
    handleClose = () => setIsModalOpen(false);
  }

  const saveContinueFunc = (response) => {
    if (response?.dataUploadId) {
      setDocumentId(response?.dataUploadId);
    }

    setIsDetailsPageOpen(true);
  };

  const myForm = new BCFormController({}, { submitSuffix: "budget/uploads", onSaveContinue: saveContinueFunc, payloadDataType: "FormData" });
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Property", parentLoanId ? `loandetails/list-loan-properties/${parentLoanId}` : "", {
    value: loanPropertyId,
    required: true
  })]);
  myForm.addCenteredGroup([myForm.getFileField("Attachment", { name: "attachmentFile", required: true })]);
  const formActions = ["save-continue", "close"];

  return <>
    {!isDetailsPageOpen && <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={handleClose} displayMessageLocally={isDisplayedInModal} />}
    {isDetailsPageOpen && <BudgetDetailsPage documentId={documentId} handleClose={handleClose} />}
  </>;
};

export const PayoffQuoteLoanCommentForm = ({ id, setIsAddCommentModalOpen, payoffQuoteOptions }) => {
  const [{ data: loanCommentInitialData, isLoading: isInitialDataLoading }] = useFetchData(`payoffquotes/payoff-temp-comments-initial-data/${id}`);
  const onAddLoanCommentSuccess = (responseData) => {
    if (payoffQuoteOptions) {
      payoffQuoteOptions.setInputs({ ...payoffQuoteOptions.inputs, lastAddedCommentId: responseData?.id || guid() });
    }
  };

  const myForm = new BCFormController(loanCommentInitialData, { 
    submitSuffix: "payoffquotes/payoff-temp-comments", 
    submitMethod: "POST", 
    dataFormatterFunc: (data) => { return {...data, id: guid(), SessionObjectUid: id}; },
    clearSuffixesOnSubmit: [`payoffquotes/payoff-temp-comments/${id}`],
    onSubmitSuccess: onAddLoanCommentSuccess
  });

  myForm.addCenteredGroup([myForm.getDropdownField("Comment Category", "metadata/list-comment-categories", { 
    name: "commentCategoryId", 
    width: 16, 
    required: true,
    disabled: true,
  })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Comment Subject", "metadata/list-comment-subjects", { 
    width: 16, 
    dependencyField: "commentCategoryId", 
    required: true,
    disabled: true,
  })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Comments", { name: "description", width: 16 })]);
  return <LmsFormGenerator 
    myForm={myForm} 
    isLoading={isInitialDataLoading} 
    actions={["save-close", "close"]}  
    handleClose={() => setIsAddCommentModalOpen(false)} 
  />;
};

export const PayoffQuoteSettlementStatementForm = ({ 
  id, 
  setIsPayoffQuoteDocumentModalOpen, 
  payoffQuoteOptions
}) => {
  const [{ data: InitialSettleMentStatementData, isLoading: isInitialDataLoading }] = useFetchData(`payoffquotes/settlement-statements-initial-data/${id}`);
  const onAddSettlementStatementSuccess = (responseData) => {
    FeatureFlags.IS_DEBUG && console.log(responseData);
    if (payoffQuoteOptions) {
      const newStatements = payoffQuoteOptions.metaData.uploadedSettlementStatements 
      ? [...payoffQuoteOptions.metaData?.uploadedSettlementStatements, responseData] 
      : [responseData];
      payoffQuoteOptions.setMetaData({ uploadedSettlementStatements: newStatements });
    }
  };

  const myForm = new BCFormController(InitialSettleMentStatementData, { 
    submitSuffix: "payoffquotes/settlement-statements", 
    submitMethod: "POST",
    payloadDataType: "FormData", 
    onSubmitSuccess: onAddSettlementStatementSuccess,
    dataFormatterFunc: (data) => {return {...data, id: guid(), sessionObjectUid: id };},
    displayMessageLocally: true
  });

  myForm.addCenteredGroup([myForm.getDropdownField("Document Master", "metadata/list-document-masters", { name: "documentMasterId", width: 16, required: true })]);
  myForm.addCenteredGroup([myForm.getInputField("Settlement Statement", { name: "assetFile", width: 16, fieldType: "file" })]);
  return <>
  <LmsFormGenerator 
    myForm={myForm} 
    isLoading={isInitialDataLoading} 
    actions={["save-close", "close"]} 
    handleClose={() => setIsPayoffQuoteDocumentModalOpen(false)}
  />
  </>;
};

export const editPayoffTransactionFormField = (
  form: BCFormController,
  isUnitPropertyModalOpen?: boolean,
  setIsUnitPropertyModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  isAddCommentModalOpen?: boolean,
  setIsAddCommentModalOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  isPayoffQuoteDocumentModalOpen?: boolean,
  setIsPayoffQuoteDocumentModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const handleDisplayRofr = () => {
    if (form?.initialValues) {
      const payoffQuote = form?.initialValues;
      if (payoffQuote.includeRofr || payoffQuote.rofrpercent || payoffQuote.roframount || payoffQuote.customizeRofr || payoffQuote.rofrpending || payoffQuote.rofrcharged || payoffQuote.rofrcustomAmount) return true;
    }
    return false;
  };

  if (handleDisplayRofr()) {
    form.addCenteredGroup([form.getLabelField("This loan has ROFR Language")]); // TODO: check with Slobodan on how to display big red text at top
  }

  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { 
      name: "specialPurposeEntityId",
      dependencyField: "borrowerOrganizationId" 
    }),
    form.getDisplayField("Loan", { name: "loanDisplay" }),
    form.getDisplayField("Escrow #", { name: "payoffEscrowNo" })
  ]);
  form.addGroupFields([
    form.getDropdownField("Fund", "metadata/list-organization-funds", { 
      name: "fundOrganizationId", 
      required: true,
      disabled: true
    }),
    form.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "bankInformationId", required: true, disabled: true }), // TODO: Sprint Work 13367
    form.getCalendarField("Payout Date"),
    form.getCalendarField("Valid Through Date", { disabled: true })
  ]);
  const refi = form?.initialValues?.refi === "No" ? false : true;
  form.addGroupFields([
    form.getCheckboxField("Refi?", { value: refi, inline: true }),
    form.getCurrencyField("Original Loan Amount", { name: "loanDetailLoanAmountOriginal", fieldType: "dollar", disabled: true }),
    form.getDisplayField("Days In Extension"),
    form.getBlankField()
  ]);

  if (form?.initialValues?.loanId) {
    // TODO: confirm endpoints, check sprint work item 13368
    !isUnitPropertyModalOpen && form.addComponent(
      <LmsTable
        apiSuffix={`payoffquotes/property-unit-reconveyance/${form?.initialValues.loanId}`}
        displayOnlyProps={["unitNumber", "actualSalesPrice"]}
        columnLabelsDictionary={{unitNumber: "Unit"}}
        isDisabled
      />
    );
  }
  if (setIsUnitPropertyModalOpen) {
    form.addCenteredGroup([
      form.getButtonField("Edit", () => setIsUnitPropertyModalOpen(true)), // TODO: sprint work item 13368 to complete modal
    ]);
  }
  
  form.addGroupFields([
    form.getCurrencyField("Current Loan Balance", { name: "loanAmountCurrent", required: true, disabled: true }),
    form.getInputField("Current Interest Rate", {
      name: "loanServicingInterestRateCurrent",
      disabled: true,
      required: true,
      fieldType: GlobalConstants.InputTypes.FLOAT as FieldTypes,
    }),
    form.getCurrencyField("Interest Reserve Balance", { required: true, disabled: true }),
  ]);

  let pdPeriod1;
  if (form?.initialValues?.pdStartDate1 && form?.initialValues?.pdEndDate1) {
    pdPeriod1 = `${convertToDateString(form?.initialValues?.pdStartDate1, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate1, "lmsdisplaydateforfieldname")}`;
  }

  let pdPeriod2;
  if (form?.initialValues?.pdStartDate2 && form?.initialValues?.pdEndDate2) {
    pdPeriod2 = `${convertToDateString(form?.initialValues?.pdStartDate2, "lmsdisplaydateforfieldname")}-${convertToDateString(form?.initialValues?.pdEndDate2, "lmsdisplaydateforfieldname")}`;
  }

  form.addGroupFields([
    form.getCurrencyField(pdPeriod1 ? pdPeriod1 : "Interest Pdstart Pdend1", { name: "interestPdstartPdend1", required: true, disabled: true }),
    form.getCurrencyField(pdPeriod2 ? pdPeriod2 : "Interest Pdstart Pdend2", { name: "interestPdstartPdend2", required: true, disabled: true, isHidden: { saleTypeOption: "Sale to purchasing company" }, isHiddenOperation: "notequal" }), 
    form.getBlankField()
  ]);
  form.addGroupFields([
    form.getInputField("Per Diem Interest", { required: true }),
    form.getCheckboxField("Prorate Extension", { inline: true }),
    form.getCurrencyField("Extension Fee", { fieldType: "dollar", required: true, disabled: true }),
    form.getInputField("Per Diem Extension", { fieldType: "dollar", required: true }),
  ]);
  form.addGroupFields([
    form.getCheckboxField("Include Sweep", { inline: true }),
    form.getCurrencyField("Sweep Amount", { fieldType: "dollar", disabled: true }),
    form.getDisplayField("Sweep % Amount", { name: "sweepPercentage", fieldType: "float" }),
    form.getBlankField()
  ]);
  form.addGroupFields([
    form.getDropdownDisplayField("Additional Interest Basis"),
    form.getDisplayField("Additional Interest Floor", { fieldType: "dollar" }),
    form.getDisplayField("Additional Interest %", { name: "additionalInterestRate", fieldType: "float" }),
    form.getCurrencyField("Additional Interest", { name: "additionalInterestAmount", disabled: true }),
  ]);
  form.addGroupFields([
    form.getCurrencyField("Reconveyance Fee", { required: true }),
    form.getDropdownField("Additional Payoff Fees Option", "metadata/list-transaction-types", { name: "transactionTypeId" }),
    form.getCurrencyField("Additional Payoff Fees Amount", { name: "additionalPayoffFee" }),
    form.getCurrencyField("Late Fee Amount"),
  ]);
  form.addGroupFields([
    form.getCheckboxField("Charge Payoff Fee", { name: "chargePayoffFeeCheckbox", value: form?.initialValues?.chargePayoffFee ? true : false }),
    form.getInputField("Charge Payoff Fee", { 
      isDisabledOn: { chargePayoffFeeCheckbox: true }, 
      isDisabledOnOperation: "notequal", 
    }),
    form.getCurrencyField("Previous Fee", { disabled: true }),
    form.getDisplayField("Total Payout", { name: "payoffQuoteAmount", fieldType: "dollar" }),
  ]);
  form.addGroupFields([
    form.getDisplayField("Payout Received", { name: "receivedAmount", fieldType: "dollar" }),
    form.getCurrencyField("Payout Residual", { name: "payoutResidual", disabled: true, fieldType: "dollar" }),
    form.getDropdownField("Residual Handling", "metadata/list-pc-residual-handling-options"),
    form.getDropdownField("Loan Sub Status", "metadata/list-paid-off-loan-sub-statuses", { name: "loanSubStatusId" }),
  ]);

  
  if (form?.initialValues.id) {
    !isAddCommentModalOpen && form.addComponent(
      <LmsTable
        apiSuffix={`payoffquotes/payoff-temp-comments/${form?.initialValues.id}`}
        displayOnlyProps={["commentSubjectDisplay", "description", "createdOn", "createdBy"]}
        columnLabelsDictionary={{commentSubjectDisplay: "Subject"}}
        title="Payoff comments"
        isDisabled
      />
    );
  }

  if (setIsAddCommentModalOpen) {
    form.addCenteredGroup([
      form.getButtonField("Add Comment", () => setIsAddCommentModalOpen(true)),
    ]);
  }

  form.addCenteredGroup([form.getTextareaField("Comments / Instructions")]);

  if (setIsPayoffQuoteDocumentModalOpen) {
    form.addCenteredGroup([
      form.getButtonField("Settlement Statement / Escrow Request", () => setIsPayoffQuoteDocumentModalOpen(true))
    ]);
  }

  if (!isPayoffQuoteDocumentModalOpen) {
  // if (isPayoffQuoteDocumentModalOpen && uploadedDocumentList?.length > 0) {
  //   // TODO: get document name, and add download
    form.addCenteredGroup([
      form.getListField("", { listOptions: ["placeholder"], listItemType: "Link", downloadMethod: "GET" })
    ]);
  // }
  }

  form.addGroupFields([
    form.getDisplayField("A Trans Balance", { fieldType: "dollar" }),
    form.getDisplayField("Unfunded Transactions", { fieldType: "dollar" }),
    form.getDisplayField("PC Funded Balance", { fieldType: "dollar" }),
    form.getDisplayField("Principal Payment To LP", { fieldType: "dollar" }),
  ]);
  form.addGroupFields([
    form.getDropdownField("Default Servicing Fee %", "metadata/list-default-percentages"),
    form.getDisplayField("Default Servicing Fee", { fieldType: "dollar" }),
    form.getDisplayField("Extension Fee After2", { fieldType: "dollar" }),
    form.getBlankField()
  ]);
  form.addGroupFields([
    form.getCheckboxField("Include PC Default Interest Split", { inline: true }),
    form.getDisplayField("Default Interest Split"),
    form.getDisplayField("Other Charges Or Credits"),
    form.getDisplayField("Loan Purchaser Accrued Interest"),
  ]);
  form.addGroupFields([
    form.getDisplayField("Per Diem"),
    form.getCalendarField("Payoff Funds Sent Date"),
    form.getDisplayField("Total Payoff", { name: "payoffQuoteAmount" }),
    form.getDisplayField(form?.initIsDisabled ? "Payout Received" : "Payout Amount Sent")
  ]);
  form.addGroupFields([
    form.getDisplayField("Payout Residual", { name: "pcPayoutResidual"}),
    form.getDropdownField("Residual Handling Option", "metadata/list-pc-residual-handling-options", { name: "pcResidualHandlingId"}),
    form.getDropdownDisplayField("Fund", { name: "pcFundOrganizationId"}),
    form.getDropdownField("Bank", "orgs/list-bank-orgs", { name: "pcBankInformationId", required: true })
  ]);
  form.addCenteredGroup([form.getTextareaField("Comments", { name: "pcDescription" })]);
  return form;
};

export const recalcReportFields = (myForm: BCFormController, isAdd = false) => {
  myForm.addGroupFields([
    myForm.getDropdownField("LP", "/metadata/list-lp-organizations", {
      name: "lpOrganizationId",
      value: "",
      required: true,
    }),
    myForm.getBlankField(),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Buyer", { name: "buyer", required: true }),
    myForm.getInputField("Seller", { name: "seller", required: true }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Servicer", { name: "servicer", required: true }),
    myForm.getInputField("Custodian", { name: "custodian", required: true }),
  ]);

  myForm.addGroupFields([
    myForm.getInputField("Diligence Agent", { name: "diligenceAgent", required: true }),
    myForm.getInputField("Account Bank", { name: "accountBank", required: true }),
  ]);

  myForm.addDividerField();
  if (!isAdd) {
    myForm.addGroupFields([
      myForm.getCalendarField("Payment Date", { name: "paymentDate", required: true, value: "" }),
    ]);
  } 
  else {
    myForm.addGroupFields([
      myForm.getCalendarField("Recalculation Date", { name: "recalculationDate", required: true, value: "" }),
    ]);
  }

  myForm.addGroupFields([
    myForm.getCalendarField("Period Start", { name: "periodStart", required: true }),
    myForm.getCalendarField("Period End", { name: "periodEnd", required: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCalendarField("Revolving Period End Date", { name: "revolvingPeriodEndDate", required: true }),
    myForm.getCalendarField("Amortization Period End Date", { name: "amortizationPeriodEndDate", required: true }),
  ],myForm.getInputField("globalRemittanceStatusId", { name: "globalRemittanceStatusId", required: true}));

  myForm.addDividerField();
  return myForm;
};

export const purchaseAdviceQuoteViewFields = (
  form, 
  isNewPurchaseAdviceQuote,
  isCompletedPurchaseAdviceQuote
) => {
  form?.initialValues?.loanId && form.addComponent(<AdviceQuoteDataDisplayTable key={form?.initialValues?.loanId} loanId={form?.initialValues?.loanId} />);
  form.addGroupFields([
    form.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    form.getDisplayField("Special Purpose Entity", { name: "specialPurposeEntityDisplay" }),
    form.getInputField("Loan", { name: "loanDisplay", disabled: true }),
    isNewPurchaseAdviceQuote === false && form.getDropdownField("Bank Organization", "bankaccount/list-banks", { name: "bankOrganizationId" }),
  ]);
  
  const today = new Date();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth()+1, 0);
  const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth()+2, 0);
  form.addGroupFields([
    form.getDisplayField("Interest Calculated On", { name: "loanDetailInterestCalculationMethodDisplay" }),
    form.getCalendarField(isNewPurchaseAdviceQuote ? "Purchase Request Date" : "Purchase Date", { 
      required: true, 
      disabled: isCompletedPurchaseAdviceQuote,
      maxDate: lastDayOfMonth.toISOString().substring(0,10)
    }),
    form.getCalendarField("Valid Through Date", { 
      required: true, 
      disabled: !isNewPurchaseAdviceQuote,
      minDate: 0,
      maxDate: lastDayOfNextMonth.toISOString().substring(0,10)
    }),
    form.getCurrencyField("Current Loan Balance", { name: "loanServicingLoanAmountCurrent", required: true, disabled: true }),
  ]);

  form.addGroupFields([
    form.getInputField("Current Interest Rate", {
      name: "loanServicingInterestRateCurrent",
      disabled: true,
      required: true,
      fieldType: GlobalConstants.InputTypes.FLOAT as FieldTypes,
    }),
    form.getCurrencyField("Interest Reserve Balance", { disabled: true, name: "loanServicingInterestReserveCurrent" }),
  ]);

  if (!isNewPurchaseAdviceQuote) {
    const interestPdstartPdend1labelFunc = (options) => { return options?.inputs?.pdPeriod1 || null; };
    const interestPdstartPdend2labelFunc = (options) => { return options?.inputs?.pdPeriod2 || null; };
    form.addGroupFields([
      form.getCurrencyField("interestPdstartPdend1", { 
        labelFunc : interestPdstartPdend1labelFunc,
        name: "interestPdstartPdend1", 
        required: true, 
        disabled: true,
        isHidden: { isPdPeriod1Display: true }, isHiddenOperation: "notequal"
      }),
      form.getCurrencyField("interestPdstartPdend2", { 
        labelFunc : interestPdstartPdend2labelFunc,
        name: "interestPdstartPdend2", 
        required: true, 
        disabled: true,
        isHidden: { isPdPeriod2Display: true }, isHiddenOperation: "notequal"
      }),
    ]);
  }
  else {
    const today = new Date();
    const pdPeriod1 = converToStartDateToEndDateString(form?.initialValues?.pdStartDate1, today.setDate(today.getDate() -1));
    form.addGroupFields([
      form.getCurrencyField(pdPeriod1, { name: "interestPdstartPdend1", required: true, disabled: true,  isHiddenFunc: ({ inputs }) => !inputs.validThroughDate }), 
    ]);
  }
  
  form.addGroupFields([
    form.getCurrencyField("Reconveyance Fee", { required: true, disabled: isCompletedPurchaseAdviceQuote }),
    form.getInputField("Additional Purchase Fee Description"),
    form.getCurrencyField("Additional Purchase Fee"),
    isNewPurchaseAdviceQuote && form.getCurrencyField("Per Diem Interest", { required: true, disabled: true }),
  ]);
  
  form.addGroupFields([
    form.getCurrencyField("Additional Interest", { name: "additionalInterestAmount", required: true, disabled: !isNewPurchaseAdviceQuote }),
    form.getDropdownField("Additional Interest Basis", "metadata/list-additional-interest-basis", { 
      name: "additionalInterestBasisId", 
      required: !isCompletedPurchaseAdviceQuote, 
      disabled: true
    }), 
    form.getCurrencyField("Additional Interest Floor", { disabled: !isNewPurchaseAdviceQuote, required: true }),
    form.getInputField("Additional Interest Rate", { disabled: !isNewPurchaseAdviceQuote, required: true }),
  ]);

  form.addComponent(<LmsTable
    data={form?.values?.loanComments ? form?.values?.loanComments : []}
    isDisabled
    displayOnlyProps={[
      "commentCategorySubjectDisplay", 
      "description", 
      "createdOn", 
      "createdBy"
    ]}
    columnLabelsDictionary={{ 
      commentCategorySubjectDisplay: "Subject", 
    }}
  />);

  form.addGroupFields([
    form.getCurrencyField("Unearned Revenue", { disabled: true }),
    form.getCurrencyField("Earned Revenue", { disabled: true }),
  ]);

  form.addGroupFields([
    form.getDisplayField("Selling Fund Organization", { name: "sellingFundOrganizationDisplay" }),
    form.getCurrencyField("Purchasers Total Purchase", { disabled: true })
  ]);

  form.addGroupFields([
    form.getCurrencyField("Amount Due To BC", { 
      name: "dueToBc", 
      disabled: true
    }),
    form.getCurrencyField("Funds Total Purchase", { 
      name: "fundTotalPurchase", 
      disabled: true
    }),
  ], {isHidden: { saleTypeOption: "Sale to purchasing company" }, isHiddenOperation: "notequal" });

  if (!isNewPurchaseAdviceQuote) {
    form.addGroupFields([
      form.getCurrencyField("Payout Received", { name: "receivedAmount", required: true }), 
      form.getCurrencyField("Payout Residual", { name: "payoutResidual", disabled: true }),
      form.getDropdownField(
        "Purchasing Company Payment", 
        "metadata/list-purchasing-company-payment", 
        { 
          name: "purchasingCompanyPaymentId", 
          isHidden: { saleTypeOption: "Sale to purchasing company" },
          isHiddenOperation: "notequal",
          isDisabledOn: { purchasingCompany: null },
          isDisabledOnOperation: "equal",
          required: true, 
          dependencyField: "purchaseOrganizationId" 
        }
      )
    ]);
  }

  form.addGroupFields([
    form.getDropdownField("Service Fee Method", "metadata/list-service-fee-methods", { name: "serviceFeeMethodId", disabled: isCompletedPurchaseAdviceQuote }),
    form.getInputField("Service Fee Percent", { 
      isHiddenFunc: ({ inputs }) => inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.BASED_ON_LOAN_AMOUNT && inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.BASED_ON_LOAN_OUTSTANDING_AMOUNT,
      required: !isCompletedPurchaseAdviceQuote,
      disabled: isCompletedPurchaseAdviceQuote
    }),
    form.getInputField("Service Fee Amount", { 
      isHiddenFunc: ({ inputs }) => inputs?.serviceFeeMethodId !== LmsServiceFeeMethod.FIXED_FEE, 
      required: true 
    }),
    form.getDropdownField("Sale Type", LmsSaleTypeOptions, { name: "saleTypeOption", required: true, disabled: isCompletedPurchaseAdviceQuote }),
    form.getDropdownField("Purchasing Company", "metadata/list-purchasing-company", { 
      name: "purchaseOrganizationId",
      isHidden: { saleTypeOption: "Sale to purchasing company" },
      isHiddenOperation: "notequal",
      disabled: isCompletedPurchaseAdviceQuote,
      required: true
    }),
  ]);

  form.addGroupFields([
    form.getDropdownField("Cross Collateralized", YesNoDropdownOptions, { 
      name: "loanCrossCollateralizedOption",
      isHidden: { saleTypeOption: "Sale to purchasing company" },
      isHiddenOperation: "notequal",
      disabled: isCompletedPurchaseAdviceQuote,
      required: !isCompletedPurchaseAdviceQuote,
    }),
    form.getInputField("Buy Rate", { 
      isHidden: { saleTypeOption: "Sale to purchasing company" }, 
      isHiddenOperation: "notequal", 
      disabled: isCompletedPurchaseAdviceQuote,
      required: !isCompletedPurchaseAdviceQuote,
    }),
    form.getDropdownField("Builders Capital Percentage", "metadata/list-builders-capital-percentages", { 
      name: "buildersCapitalPercentageId",
      isHidden: { saleTypeOption: "Sale to purchasing company" },
      isHiddenOperation: "notequal",
      disabled: isCompletedPurchaseAdviceQuote,
      required: !isCompletedPurchaseAdviceQuote
    }),
    form.getDropdownField("Notional Builders Capital Percentage", "metadata/list-notional-builders-capital-percentages", { 
      name: "notionalBuildersCapitalPercentageId",
      isHidden: { saleTypeOption: "Sale to purchasing company",  buildersCapitalPercentageId: "fb561aef-624d-4af9-bd2c-a99c007919de", },
      isHiddenOperation: "notequal",
      disabled: isCompletedPurchaseAdviceQuote
    }),
  ]);

  if (form?.initIsDisabled) {
    form.addGroupFields([
      form.getDisplayField("Amortization Option", { name: "amortizationOption" }),
      form.getDropdownField("Revenue Recognition Method", LmsAllocationMethodOptions, { disabled: !isNewPurchaseAdviceQuote, name: "recognitionOption", required: true }),
      form.getDisplayField("Fund"),
      form.getDisplayField("Collateral Bank Organization", { name: "collateralBank", required: true })
    ]);
  }
  else {
    form.addGroupFields([
      form.getDropdownField("Amortization Option", YesNoDropdownOptions, { name: "amortizationOption", required: true, disabled: isCompletedPurchaseAdviceQuote }),
      form.getDropdownField("Revenue Recognition Method", LmsAllocationMethodOptions, { disabled: !isNewPurchaseAdviceQuote, name: "recognitionOption", required: true }),
      form.getDropdownField("Fund", "metadata/list-organization-funds", { 
        name: "fundOrganizationId", 
        disabled: isCompletedPurchaseAdviceQuote,
        required: true
      }),
      form.getDropdownField("Collateral Bank Organization", "purchasequotes/list-collateral-bank-orgs", { name: "collateralBankOrganizationId"})
    ]);
  }

  const purchaseDate = isNewPurchaseAdviceQuote ? "purchaseRequestDate" : "purchaseDate";
  if (form?.initIsDisabled === false) {
    form.addCenteredGroup([
      form.getButtonField("Earned / Unearned", undefined, {
        modalSize: "fullscreen",
        modalContent: (setIsAddCommentModalOpen, options) => {
          const message = [] as string[];
          if (!options.inputs.recognitionOption) {
            message.push("Please select a value for recognition option.");
          }

          if (!options.inputs.amortizationOption) {
            message.push("Please select an amortization option.");
          }

          if (!options.inputs[purchaseDate]) {
            message.push(`${isNewPurchaseAdviceQuote ? "Purchase Request Date" : "Purchase Date" } is required.`);
          }

          if (message.length > 0) {
            options.setFormErrorMessage({ message: [...message] });
            setIsAddCommentModalOpen(false);
          }
          else {
            return <EarnedUnearnedModalForm
              mainFormOptions={options}
              setIsAddModalFormOpen={setIsAddCommentModalOpen}
            />;
          }
        }
      }),
      form.getButtonField("Transactions", undefined, {
        modalSize: "fullscreen",
        modalContent: (setIsTransactionsModalOpen, options) => {
          const inputs = options?.inputs;
          const messages = [] as string[];
          if (!inputs[purchaseDate]) {
            messages.push(`${isNewPurchaseAdviceQuote ? "Purchase Request Date" : "Purchase Date" } is required.`);
          }

          if (!inputs.purchaseOrganizationId) {
            messages.push("Please select a Purchasing Company.");
          }

          if (!inputs.buildersCapitalPercentageId) {
            messages.push("Please select a value for Builders Capital Percentage.");
          }

          if (messages.length > 0) {
            options.setFormErrorMessage({ message: [...messages] });
            setIsTransactionsModalOpen(false);
          }
          else {
            return <TransactionsModalForm
              mainFormOptions={options}
              setIsTransactionsModalOpen={setIsTransactionsModalOpen}
            />;
          }
        },
        isHidden: { saleTypeOption: "Sale to purchasing company" },
        isHiddenOperation: "notequal", 
      })
    ]);
  }

  form.addComponent((options) => 
    <RevenueRecognitionDisplayTable 
      key={form?.initialValues.id} 
      mainFormOptions={options} 
  />, { isHidden: { revenueRecognitionTempId: null }, isHiddenOperation: "equal" });

  form.addComponent((options) => 
    <PurchaseQuoteLoanTransactionTable key={form?.initialValues.id} mainFormOptions={options} />,
    {
      isHidden: { saleTypeOption: "Sale to purchasing company" },
      isHiddenOperation: "notequal", 
    }
  );

  if (isNewPurchaseAdviceQuote) {
    form.addCenteredGroup([form.getTextareaField("Description")]);
  }

  return form;
};

export const earnedUnearnedModalFields = (form: BCFormController, revenueRecognitionData): BCFormController => {
  form.addCenteredGroup([
    form.getDisplayField("Loan", { disabled: true }),
    form.getCalendarField("Purchase Date", { disabled: true }),
  ]);

  form.addGroupFields([
    form.getLabelField("Name",{ width: 2, position: "center" }),
    form.getLabelField("Recognized Days", { width: 2, position: "center" }),
    form.getLabelField("Recognized Amount", { width: 2, position: "center" }),
    form.getLabelField("Unrecognized Days", { width: 2, position: "center", wrapLabel: true }),
    form.getLabelField("Unrecognized Amount", { width: 2, position: "center", wrapLabel: true }),
    form.getLabelField("Allocation Method", { width: 2, position: "center" }),
    form.getLabelField("Extra Revenue Amount", { width: 2, position: "center", wrapLabel: true }),
    form.getLabelField("Extra Revenue Percent", { width: 2, position: "center", wrapLabel: true })
  ], { widths: 1 });
  
  if (revenueRecognitionData?.length) {
    revenueRecognitionData.map((rowData, i) => {
      const { 
        name, 
        recognizedDays, 
        recognizedAmount, 
        unrecognizedDays, 
        unrecognizedAmount,
        allocationMethodOption,
        extraRevenueAmount,
        extraRevenuePercent
      } = rowData;

      const formRowFields = [] as any[];
      formRowFields.push(form.getInputField("", { width: 2, name: "name", value: name, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getInputField("", { width: 2, name: "recognizedDays", value: recognizedDays, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "recognizedAmount", value: recognizedAmount, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getInputField("", { width: 2, name: "unrecognizedDays", value: unrecognizedDays, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "unrecognizedAmount", value: unrecognizedAmount, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getDropdownField("", LmsAllocationMethodOptions, 
        { width: 2, name: "allocationMethodOption", value: allocationMethodOption, nestedInputsProp: i }
      ));
      formRowFields.push(form.getCurrencyField("", 
        { 
          width: 2,
          name: "extraRevenueAmount", 
          value: extraRevenueAmount, 
          nestedInputsProp: i,
          isDisabledOn: { allocationMethodOption: "Dollar Amount" },
          isDisabledOnOperation: "notequal" 
        }
      ));
      formRowFields.push(form.getInputField("", 
        { 
          width: 2,
          name: "extraRevenuePercent", 
          value: extraRevenuePercent, 
          nestedInputsProp: i, 
          isDisabledOn: { allocationMethodOption: "% Value" },
          isDisabledOnOperation: "notequal" 
        }
      ));
      form.addGroupFields(formRowFields, { widths: 1 });
    });
  }

  return form;
};

interface IEarnedUnearnedModalFormProps {
  mainFormOptions:any;
  setIsAddModalFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccessFunc?: (any) => Promise<void>;
}

export const EarnedUnearnedModalForm = ({
  mainFormOptions,
  setIsAddModalFormOpen
}: IEarnedUnearnedModalFormProps) => {
  const [{ fetchData }] = useFetchData("");
  const onRevenueRecognitionChanged = (options, lastUpdated, nestedProp) => {
    const inputs = options?.inputs || {};
    if (lastUpdated === "extraRevenueAmount") {
      if (inputs[nestedProp].extraRevenueAmount) {
        const userInput = parseInt(inputs[nestedProp].extraRevenueAmount);
        const maxAmount = parseInt(inputs[nestedProp].unrecognizedAmount) || 0;
        const validAmount = userInput > maxAmount ? maxAmount : userInput;
        options.setInputs({ ...inputs, [nestedProp]: { ...inputs[nestedProp], extraRevenueAmount: validAmount } });
      }
    }

    if (lastUpdated === "extraRevenuePercent") {
      const userInput = inputs[nestedProp].extraRevenuePercent ? parseInt(inputs[nestedProp].extraRevenuePercent) : 0;
      const validPercent = userInput > 100 ? 100 : userInput;
      options.setInputs({ ...inputs, 
        [nestedProp]: { 
          ...inputs[nestedProp],
          extraRevenuePercent: validPercent,
          extraRevenueAmount: inputs[nestedProp].unrecognizedAmount ? (inputs[nestedProp].unrecognizedAmount * ( validPercent / 100 )) : 0,
        } 
      });
    }
  };

  const handleInitLoad = async (modalOptions) => {
    const inputs = mainFormOptions.inputs;
    let recognitionTempId = inputs.revenueRecognitionTempId;
    let error;
    if (!inputs.revenueRecognitionTempId) {
      const { data: revenueRecognitionTempIdResult, getTempIdError } = await fetchData("purchasequotes/revenue-recognition-temp-id", { 
        fetchMethod: "POST", 
        skipCache: true,
        data: {
          "loanId": inputs.loanId,
          "recognitionOption": inputs?.recognitionOption,
          "amortizationOption": inputs?.amortizationOption,
          "purchaseRequestDate": inputs?.purchaseRequestDate
        } 
      });

      recognitionTempId = revenueRecognitionTempIdResult;
      if (getTempIdError) {
        error = getTempIdError;
      }
    }

    if (!error && recognitionTempId) {
      const { data: revenueRecognitionModalData } = await fetchData(`purchasequotes/revenue-recognition-temp-details/${recognitionTempId}`, { fetchMethod: "GET", skipCache: true });
      mainFormOptions.setInputs({
        ...inputs,
        revenueRecognitionTempId: recognitionTempId,
        revenueRecognitionModalData: [...revenueRecognitionModalData],
        showRevenueRegenerateMessage: false
      });
    }
    else {
      modalOptions?.useFormInstance?.setFormErrorMessage(error);
    }
  };

  const handleFormatSubmitRevenueChange = (inputs) => {
    const updatedModalData = [] as any;
    mainFormOptions?.inputs?.revenueRecognitionModalData?.forEach((item, i) => {
      updatedModalData.push({ ...item, ...inputs[i], revenueRecognitionTempId: mainFormOptions?.inputs?.revenueRecognitionTempId });
    });

    return [ ...updatedModalData ];
  };

  const handleOnSubmitRevenueModalSuccess = async (response, options) => {
    if (response) {
      const { data: revenueValue, error } = await fetchData(`purchasequotes/revenue-recognition-temp-data/${response?.[0]?.revenueRecognitionTempId}`);
      if (revenueValue) {
        mainFormOptions.setInputs((prevInputs) => {
          return { 
            ...prevInputs, 
            revenueRecognitionTempId: response?.[0]?.revenueRecognitionTempId,
            revenueRecognitionModalData: response,
            earnedRevenue: revenueValue?.earnedRevenue,
            unearnedRevenue: revenueValue?.unearnedRevenue,
            revenueRecognitionTableData: response
          };
        });
      }
      else {
        options?.useFormInstance?.setFormErrorMessage(error);
      }
    }
  };

  const myForm = earnedUnearnedModalFields(
    new BCFormController({ 
      loan: mainFormOptions?.inputs?.loanDisplay, 
      purchaseDate: mainFormOptions?.inputs?.purchaseDate ? mainFormOptions?.inputs?.purchaseDate : mainFormOptions?.inputs?.purchaseRequestDate
    }, {
      submitSuffix: "purchasequotes/revenue-recognition-temp-details",
      submitMethod: "PUT",
      dataFormatterFunc: (data) => handleFormatSubmitRevenueChange(data),
      onSubmitSuccess: (response, options) => handleOnSubmitRevenueModalSuccess(response, options),
      displayMessageLocally: true
    }), 
    mainFormOptions?.inputs?.revenueRecognitionModalData
  );

  return (<>
    <LmsFormGenerator
      myForm={myForm}
      actions={["save", "save-close", "close"]}
      handleClose={(): void => setIsAddModalFormOpen(false)}
      onFormChanged={onRevenueRecognitionChanged}
      onInitLoad={(modalOptions) => handleInitLoad(modalOptions)}
      isLoading={!mainFormOptions?.inputs?.revenueRecognitionModalData}
    />
  </>);
};

export const loanTransactionModalFields = (form: BCFormController, loanTransactionData): BCFormController => {
  form.addCenteredGroup([
    form.getDisplayField("Loan"),
    form.getInputField("A Piece", { fieldType: "float", disabled: true }),
    form.getInputField("B Piece", { fieldType: "float", disabled: true }),
    form.getInputField("Notional B Piece", { name: "notionalBPiece", fieldType: "float", disabled: true })
  ]);

  form.addGroupFields([
    form.getLabelField("Transaction Category", { width: 2, position: "center" }),
    form.getLabelField("Transaction Type", { width: 2, position: "center" }),
    form.getLabelField("Expect Payment", { width: 1, position: "center", wrapLabel: true }),
    form.getLabelField("Add To Loan Balance", { width: 1, position: "center", wrapLabel: true }),
    form.getLabelField("Transaction Effective Date", { width: 2, position: "center", wrapLabel: true }),
    form.getLabelField("Transaction Amount", { width: 2, position: "center" }),
    form.getLabelField("Custom Amount", { width: 2, position: "center" }),
    form.getLabelField("Transaction Split", { width: 2, position: "center" }),
    form.getLabelField("A Piece", { width: 2, position: "center" }),
    form.getLabelField("B Piece", { width: 2, position: "center" }),
    form.getLabelField("Notional Piece", { width: 2, position: "center" }),
    form.getLabelField("Running A Commitment", { width: 2, position: "center" }),
    form.getLabelField("Total Loan", { width: 2, position: "center" }),
    form.getLabelField("(A & B)?", { width: 2, position: "center" }),
    form.getLabelField("Potential Loan Revenues", { width: 2, position: "center" }),
    form.getLabelField("BC Loan Receivable", { width: 2, position: "center" }),
    form.getLabelField("Total BC Receivables", { width: 2, position: "center" })
  ], { widths: 1 });

  if (loanTransactionData) {
    let [runningACommitment, totalLoan, aAndB, potentialLoanRevenues, bcLoanReveivable, totalBcReceivables] = Array(6).fill(0);
    loanTransactionData.map((rowData, i) => {
      const { 
        transactionCategory, 
        transactionType, 
        expectPayment,
        addToLoanBalance,
        transactionEffectiveDate,
        transactionAmount,
        customAmount,
        apiece,
        bpiece,
        notionalBpiece,
      } = rowData;
      runningACommitment += apiece;
      totalLoan += transactionAmount;
      aAndB += apiece + bpiece + notionalBpiece;
      potentialLoanRevenues = totalLoan - aAndB;
      bcLoanReveivable = totalLoan - runningACommitment;
      totalBcReceivables = notionalBpiece ? potentialLoanRevenues : bcLoanReveivable;
      rowData = {...rowData, 
        runningACommitment: runningACommitment, 
        totalLoan: totalLoan, 
        aAndB: aAndB, 
        potentialLoanRevenues: potentialLoanRevenues,
        bcLoanReveivable: bcLoanReveivable,
        totalBcReceivables: totalBcReceivables
      };

      const formRowFields = [] as any[];
      formRowFields.push(form.getInputField("", { width: 2, name: "category", value: transactionCategory, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getInputField("", { width: 2, name: "transactionType", value: transactionType, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getInputField("", { width: 1, name: "expectPayment", value: expectPayment, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getInputField("", { width: 1, name: "addToLoanBalance", value: addToLoanBalance, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCalendarField("", { width: 2, name: "transactionEffectiveDate", value: transactionEffectiveDate, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "transactionAmount", value: transactionAmount, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "customAmount", value: customAmount, nestedInputsProp: i }));
      formRowFields.push(form.getDropdownField("", LmsTransactionSplitOptions, { width: 2, name: "transactionSplit", value: "Split", nestedInputsProp: i }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "apiece", value: apiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "bpiece", value: bpiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "notionalBpiece", value: notionalBpiece, nestedInputsProp: i, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "runningACommitment", value: runningACommitment, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "totalLoan", value: totalLoan, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "aAndB", value: aAndB, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "potentialLoanRevenues", value: potentialLoanRevenues, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "BcLoanReveivable", value: bcLoanReveivable, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      formRowFields.push(form.getCurrencyField("", { width: 2, name: "totalBcReceivables", value: totalBcReceivables, nestedInputsProp: i, wrapLabel: true, disabled: true }));
      form.addGroupFields(formRowFields, { widths: 1 });
    });

      form.addGroupFields([
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 1, position: "center", wrapLabel: true }),
      form.getLabelField("", { width: 1, position: "center", wrapLabel: true }),
      form.getLabelField("", { width: 2, position: "center", wrapLabel: true }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("Totals", { width: 2, position: "center" }),
      form.getCurrencyField("", { width: 2, name: "aPieceTotal", disabled: true }),
      form.getCurrencyField("", { width: 2, name: "bPieceTotal", disabled: true }),
      form.getCurrencyField("", { width: 2, name: "notionalBpieceTotal", disabled: true }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" }),
      form.getLabelField("", { width: 2, position: "center" })
    ], { widths: 1 });
  }

  return form;
};

export const TransactionsModalForm = ({
  mainFormOptions,
  setIsTransactionsModalOpen
}) => {
  const mainFormInputs = mainFormOptions?.inputs;
  const [{ data: titleInfo, isLoading: istitleInfoLoading, fetchData }] = useFetchData(`purchasequotes/loan-transaction-temp-initial-data/${mainFormInputs?.purchaseQuoteLoanTransactionTempId}`, { skipCache: true }); 
  const handleInitTransactionLoad = async (modalOptions) => {
    const { data: loanTransactionDetails, error } = await fetchData(`purchasequotes/loan-transaction-temp-details/${mainFormInputs?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET", skipCache: true });
    if (!error && loanTransactionDetails) {
      mainFormOptions.setInputs({
        ...mainFormInputs,
        loanTransactionModalData: [...loanTransactionDetails],
      });
    }
    else {
      modalOptions?.useFormInstance?.setFormErrorMessage(error);
    }
  };

  const onTransactionsModalChanged = (options, lastUpdated, nestedProp) => {
    const inputs = options?.inputs || {};
    const inputPiece = inputs[nestedProp].customAmount ? parseInt(inputs[nestedProp].customAmount) : 0;
    const remainingPiece = inputs[nestedProp].transactionAmount ? inputs[nestedProp].transactionAmount - inputPiece : inputPiece * -1;
    const updatedNestedProp = { ...inputs[nestedProp], notionalBpiece: null, isUpdated: true };
    if (lastUpdated === "customAmount" || lastUpdated === "transactionSplit") {
      inputs.aPieceTotal -= inputs[nestedProp].apiece ? inputs[nestedProp].apiece : 0;
      inputs.bPieceTotal -= inputs[nestedProp].bpiece ? inputs[nestedProp].bpiece : 0;
      inputs.notionalBpieceTotal -= inputs[nestedProp].notionalBpiece ? inputs[nestedProp].notionalBpiece : 0;
      if (inputs[nestedProp].customAmount && inputs[nestedProp]?.transactionSplit !== "Split") {
        if (inputs[nestedProp].transactionSplit === "A Piece") {
          updatedNestedProp.apiece = inputPiece;
          updatedNestedProp.bpiece = remainingPiece;
        }

        if (inputs[nestedProp].transactionSplit === "B Piece") {
          updatedNestedProp.apiece = remainingPiece;
          updatedNestedProp.bpiece = inputPiece;
        }
      }

      if (!inputs[nestedProp].customAmount) {
        if (inputs[nestedProp]?.transactionSplit === "A Piece") {
          updatedNestedProp.apiece = inputs[nestedProp].transactionAmount;
          updatedNestedProp.bpiece = 0;
        }

        if (inputs[nestedProp]?.transactionSplit === "B Piece") {
          updatedNestedProp.apiece = 0;
          updatedNestedProp.bpiece = inputs[nestedProp].transactionAmount;
          updatedNestedProp.notionalBpiece = 0;
        }
      }
    }

    inputs.aPieceTotal += updatedNestedProp.apiece ? updatedNestedProp.apiece : 0;
    inputs.bPieceTotal += updatedNestedProp.bpiece ? updatedNestedProp.bpiece : 0;
    inputs.notionalBpieceTotal += updatedNestedProp.notionalBpiece ? updatedNestedProp.notionalBpiece : 0;
    options.setInputs({ ...inputs, [nestedProp]: { ...updatedNestedProp }});
  };

  const handleFormatSubmitTransactionData = (inputs) => {
    const updatedModalData = [] as any;
    mainFormInputs?.loanTransactionModalData?.forEach((item, i) => {
      if (inputs[i].isUpdated) {
        updatedModalData.push({ ...item, ...inputs[i]});
      }
    });

    return updatedModalData;
  };

  const handleOnSubmitTransactionSuccess = async (response, options) => {
    const { data: loanTransactionData, error } = await fetchData(`purchasequotes/loan-transaction-temp-data/${mainFormInputs?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET" });
    if (!error && loanTransactionData) {
      mainFormOptions.setInputs((prevInputs) => {
        return { ...prevInputs, loanTransactionTableData: loanTransactionData, loanTransactionModalData: response };
      });
    }
    else {
      options?.useFormInstance?.setFormErrorMessage(error);
    }
  };

  const handleMainFormValues = () => {
    let aPieceTotal = 0, bPieceTotal = 0, notionalBpieceTotal = 0;
    mainFormInputs?.loanTransactionModalData?.forEach(data => {
      aPieceTotal += data.apiece ? data.apiece : 0;
      bPieceTotal += data.bpiece ? data.bpiece : 0;
      notionalBpieceTotal += data.notionalBpiece ? data.notionalBpiece : 0;
    });

    return {
      loan: mainFormInputs?.loanDisplay,
      aPieceTotal: aPieceTotal,
      bPieceTotal: bPieceTotal,
      notionalBpieceTotal: notionalBpieceTotal
    };
  };

  const myForm = loanTransactionModalFields(new BCFormController(titleInfo, {
      values: handleMainFormValues(),
      isCompact: true, 
      styles: { minWidth: "2500px" },
      submitSuffix: "purchasequotes/loan-transaction-temp-details",
      submitMethod: "PUT",
      dataFormatterFunc: (data) => handleFormatSubmitTransactionData(data),
      onSubmitSuccess: (response, options) => handleOnSubmitTransactionSuccess(response, options),
      displayMessageLocally: true
    }),
    mainFormInputs?.loanTransactionModalData
  );

  return (<>
    <LmsFormGenerator
      myForm={myForm}
      actions={["save", "save-close", "close"]}
      onFormChanged={onTransactionsModalChanged}
      handleClose={(): void => setIsTransactionsModalOpen(false)}
      onInitLoad={(modalOptions) => handleInitTransactionLoad(modalOptions)}
      isLoading={istitleInfoLoading || !mainFormInputs?.loanTransactionModalData}
    />
  </>);
};
